import type { ScopeRef } from "@enduring-systems/entitlements";
import { UNLIMITED } from "@enduring-systems/entitlements";

import type { EffectiveVsGranted } from "./entitlements-api";
import { scopeKindLabel } from "./scope";

/**
 * The engine reports a TAGGED value (`{ kind, value }`); the admin store reports the
 * raw value with its type alongside. Both halves are formatted through the same
 * function, so the tagged one is unwrapped first rather than rendered as an object.
 */
export interface TaggedValue {
  readonly kind: string;
  readonly value: unknown;
}

export function readTaggedValue(value: unknown): TaggedValue | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const candidate = value as Record<string, unknown>;
  if (typeof candidate["kind"] !== "string" || !("value" in candidate)) {
    return null;
  }
  return { kind: candidate["kind"], value: candidate["value"] };
}

/**
 * `typeKind` decides the wording, not the runtime type of the value: an enum member
 * may legitimately be the string "unlimited", which means the member, not an
 * uncapped quota.
 */
export function formatEntitlementValue(typeKind: string, value: unknown): string {
  if (typeKind === "quota" && value === UNLIMITED) {
    return "Unlimited";
  }
  if (typeKind === "boolean") {
    return value ? "Enabled" : "Disabled";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value) ?? "unreadable value";
}

/**
 * Takes a `string`, not `PrecedenceTier`: the admin-api's guard validates only that
 * the wire field is a string, so narrowing it here would render `undefined` for any
 * tier this console has not been taught. An unrecognized tier shows itself.
 */
export function precedenceTierLabel(tier: string): string {
  switch (tier) {
    case "override":
      return "Override";
    case "bundle":
      // The engine's base layer is "bundle"; the editor writes it as "base", so the
      // operator is shown the word they set it with.
      return "Base grant";
    case "default":
      return "Capability default";
    default:
      return tier;
  }
}

/**
 * How a grant was written, in the operator's words. This is the STORED tier
 * ("base" / "override"), not the engine's precedence tier, which names the base
 * layer "bundle" (see `precedenceTierLabel`). Shared by the resolution view and
 * the audit log so the same stored value cannot end up worded two ways.
 *
 * Only the two known tiers are mapped. An audit row whose tier could not be read
 * arrives as "unknown", and calling that a base grant would be a factual claim
 * about how someone wrote a grant, made up from a missing field. Anything else
 * shows itself, like `precedenceTierLabel`.
 */
export function grantTierLabel(tier: string): string {
  switch (tier) {
    case "override":
      return "Override";
    case "base":
      return "Base grant";
    default:
      return tier;
  }
}

export function scopeLabel(scope: ScopeRef | null): string {
  if (scope === null) {
    return "the capability default";
  }
  if (scope.kind === "platform") {
    return scopeKindLabel("platform");
  }
  return `${scopeKindLabel(scope.kind)} ${scope.id}`;
}

/**
 * Why the effective value is what it is. Derived from the two facts the server
 * reports (`inheritedFromElsewhere` and the source scope) plus whether anything is
 * set at the selected scope, never from a second pass over precedence.
 *
 * A clamp and an inheritance are the same server flag but different answers: with
 * nothing set here the value is simply inherited, while a value set here that lost
 * to an ancestor means that ancestor's ceiling binds (only quotas clamp).
 */
export type ResolutionExplanation =
  | { readonly kind: "in-force" }
  | { readonly kind: "default" }
  | { readonly kind: "inherited"; readonly from: ScopeRef }
  | { readonly kind: "clamped"; readonly by: ScopeRef; readonly valueKind: string }
  | { readonly kind: "unavailable"; readonly reason: string };

/** A resolved envelope whose value cannot be read is still not an answer. */
export const UNREADABLE_EFFECTIVE = "the effective value was not readable";

export function explainResolution(view: EffectiveVsGranted): ResolutionExplanation {
  if (!view.effective.resolved) {
    return { kind: "unavailable", reason: view.effective.reason };
  }
  const tagged = readTaggedValue(view.effective.value);
  if (tagged === null) {
    return { kind: "unavailable", reason: UNREADABLE_EFFECTIVE };
  }
  if (!view.inheritedFromElsewhere) {
    return { kind: "in-force" };
  }
  const source = view.effective.source.scope;
  if (source === null) {
    return { kind: "default" };
  }
  return view.granted === null
    ? { kind: "inherited", from: source }
    : { kind: "clamped", by: source, valueKind: tagged.kind };
}
